import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from '@/shared/api/client'
import type { Translate } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { uuidSchema } from '@/shared/api/schemas'
import { seededName } from '@/shared/lib/seededName'

export const REFERENCE_CHARSETS = ['ANY', 'DIGITS', 'ALPHANUMERIC'] as const
export type ReferenceCharset = (typeof REFERENCE_CHARSETS)[number]

export const TRANSFER_TYPE_CODE = /^[A-Z][A-Z0-9_]{1,31}$/
export const TRANSFER_TYPE_NAME_MAX = 60
export const TRANSFER_REFERENCE_MAX = 120
export const TRANSFER_TYPE_SORT_MAX = 1000
export const CARRIED = 'CARRIED'

const CHARSET_PATTERN: Record<ReferenceCharset, RegExp> = {
  ANY: /^.+$/u,
  DIGITS: /^[0-9]+$/,
  ALPHANUMERIC: /^[\p{L}0-9]+$/u,
}

const transferTypeSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  referenceRequired: z.boolean(),
  referenceLabel: z.string().nullish(),
  referenceCharset: z.enum(REFERENCE_CHARSETS),
  referenceMinLength: z.number().nullish(),
  referenceMaxLength: z.number().nullish(),
  sortOrder: z.number(),
  system: z.boolean(),
  active: z.boolean(),
})

export type CashTransferType = z.infer<typeof transferTypeSchema>

export interface TransferTypeDraft {
  code: string
  name: string
  referenceRequired: boolean
  referenceLabel: string | null
  referenceCharset: ReferenceCharset
  referenceMinLength: number | null
  referenceMaxLength: number | null
  sortOrder: number
  active: boolean
}

export async function fetchTransferTypes(signal?: AbortSignal): Promise<CashTransferType[]> {
  const response = await api.get<unknown>('/api/cashdesk/transfer-types', { ...(signal ? { signal } : {}) })
  return z.array(transferTypeSchema).parse(response.data)
}

export async function createTransferType(draft: TransferTypeDraft): Promise<CashTransferType> {
  const response = await api.post<unknown>('/api/cashdesk/transfer-types', draft)
  return transferTypeSchema.parse(response.data)
}

export async function updateTransferType(id: string, draft: TransferTypeDraft): Promise<CashTransferType> {
  const response = await api.put<unknown>(`/api/cashdesk/transfer-types/${id}`, draft)
  return transferTypeSchema.parse(response.data)
}

export function useTransferTypes(enabled = true) {
  return useQuery({
    queryKey: queryKeys.cashdesk.transferTypes,
    queryFn: ({ signal }) => fetchTransferTypes(signal),
    enabled,
    staleTime: 5 * 60_000,
  })
}

export function transferTypeName(
  code: string | null | undefined,
  types: readonly CashTransferType[] | undefined,
  t: Translate,
): string {
  if (!code) return t('common.dash')
  const type = types?.find((candidate) => candidate.code === code)
  return seededName('transferTypes', code, type?.name ?? t(`seed.transferTypes.${code}`, { defaultValue: code }), t)
}

export function referenceLabel(type: CashTransferType, t: Translate): string {
  return type.referenceLabel
    ? seededName('transferTypeReferences', type.code, type.referenceLabel, t)
    : t('cashdesk.transfer.externalReference')
}

export function referenceProblem(type: CashTransferType, value: string): 'short' | 'long' | 'format' | null {
  const reference = value.replace(/[\p{Z}\p{Cc}\p{Cf}]+/gu, ' ').trim()
  if (reference.length > (type.referenceMaxLength ?? TRANSFER_REFERENCE_MAX)) return 'long'
  if (reference.length < (type.referenceMinLength ?? 1)) return 'short'
  return CHARSET_PATTERN[type.referenceCharset].test(reference) ? null : 'format'
}
